// You can write more code here

/* START OF COMPILED CODE */

import StudentGame from '../../GameComponents/PhaserGame/StudentGames/StudentGame';
import DefaultButton from '../../prefabs/GeneralUI/Buttons/DefaultButton';
import HUDButton from '../../prefabs/GeneralUI/Buttons/HUDButton';
import DefinitionExampleView from '../../prefabs/GeneralUI/DefinitionExampleView';
import PlayerTypingInput from '../../prefabs/StudentGame/PlayerTypingInput';
import SequencedCardDropSite from '../../prefabs/StudentGame/SequencedCardDropSite';
/* START-USER-IMPORTS */
import { ControllerType } from '@/common/types/student';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Duel from '@/game/scenes/DuelScenes/Duel';
import DuelWordCreationPanelController from '@/game/scenes/DuelScenes/DuelWordCreationPanelController';
/* END-USER-IMPORTS */

export default class DuelWordCreationPanel extends StudentGame {
  constructor() {
    super('DuelWordCreationPanel');

    /* START-USER-CTR-CODE */
    this._controller = new DuelWordCreationPanelController(this);
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {
    // mainContainer
    const mainContainer = this.add.container(0, 0);

    // targetWordPlaceholder
    const targetWordPlaceholder = this.add.bitmapText(0, -528, 'Aronui', '');
    targetWordPlaceholder.setOrigin(0.5, 0.5);
    targetWordPlaceholder.fontSize = -64;
    mainContainer.add(targetWordPlaceholder);

    // viewsContainer
    const viewsContainer = this.add.container(0, 0);
    mainContainer.add(viewsContainer);

    // typingView
    const typingView = new PlayerTypingInput(this, 0, -256);
    viewsContainer.add(typingView);

    // cardView
    const cardView = this.add.container(0, -140);
    viewsContainer.add(cardView);

    // boardDropSite
    const boardDropSite = new SequencedCardDropSite(this, 0, -288);
    cardView.add(boardDropSite);

    // handDropSite
    const handDropSite = new SequencedCardDropSite(this, 0, -112);
    cardView.add(handDropSite);

    // playerInputView
    const playerInputView = this.add.container(0, 0);
    viewsContainer.add(playerInputView);

    // nineslice_1
    const nineslice_1 = this.add.nineslice(
      0,
      -84,
      'Hud3',
      undefined,
      88,
      40,
      10,
      10,
      10,
      10,
    );
    nineslice_1.scaleX = 4;
    nineslice_1.scaleY = 4;
    playerInputView.add(nineslice_1);

    // submitButton
    const submitButton = new DefaultButton(this, 0, -120);
    playerInputView.add(submitButton);

    // gameButtonsContainer
    const gameButtonsContainer = this.add.container(0, -56);
    gameButtonsContainer.scaleX = 2;
    gameButtonsContainer.scaleY = 2;
    playerInputView.add(gameButtonsContainer);

    // autoWinButton
    const autoWinButton = new HUDButton(this, 56, 0, 'ui-submit-button');
    gameButtonsContainer.add(autoWinButton);

    // playWordInSentenceButton
    const playWordInSentenceButton = new HUDButton(
      this,
      -56,
      0,
      'ui-speak-button',
    );
    gameButtonsContainer.add(playWordInSentenceButton);

    // playDefinitionButton
    const playDefinitionButton = new HUDButton(
      this,
      -20,
      0,
      'ui-definition-button',
    );
    gameButtonsContainer.add(playDefinitionButton);

    // repeatInstructionButton
    const repeatInstructionButton = new HUDButton(
      this,
      20,
      0,
      'ui-repeat-button',
    );
    gameButtonsContainer.add(repeatInstructionButton);

    // definitionExampleView
    const definitionExampleView = new DefinitionExampleView(this, 0, 0);
    this.add.existing(definitionExampleView);

    // submitButton (prefab fields)
    submitButton.buttonText = 'Submit';

    // autoWinButton (prefab fields)
    autoWinButton.useAlternateTextures = false;

    // playWordInSentenceButton (prefab fields)
    playWordInSentenceButton.useAlternateTextures = false;

    // playDefinitionButton (prefab fields)
    playDefinitionButton.useAlternateTextures = false;

    // repeatInstructionButton (prefab fields)
    repeatInstructionButton.useAlternateTextures = false;

    this.typingView = typingView;
    this.boardDropSite = boardDropSite;
    this.handDropSite = handDropSite;
    this.cardView = cardView;
    this.submitButton = submitButton;
    this.autoWinButton = autoWinButton;
    this.playWordInSentenceButton = playWordInSentenceButton;
    this.playDefinitionButton = playDefinitionButton;
    this.repeatInstructionButton = repeatInstructionButton;
    this.gameButtonsContainer = gameButtonsContainer;
    this.mainContainer = mainContainer;
    this.definitionExampleView = definitionExampleView;

    this.events.emit('scene-awake');
  }

  private typingView!: PlayerTypingInput;
  private boardDropSite!: SequencedCardDropSite;
  private handDropSite!: SequencedCardDropSite;
  private cardView!: Phaser.GameObjects.Container;
  public submitButton!: DefaultButton;
  public autoWinButton!: HUDButton;
  public playWordInSentenceButton!: HUDButton;
  public playDefinitionButton!: HUDButton;
  public repeatInstructionButton!: HUDButton;
  public gameButtonsContainer!: Phaser.GameObjects.Container;
  public mainContainer!: Phaser.GameObjects.Container;
  private definitionExampleView!: DefinitionExampleView;

  /* START-USER-CODE */

  private _controller: DuelWordCreationPanelController;
  get controller() {
    return this._controller;
  }

  get currentTargetWord() {
    return this._controller.currentTargetWord;
  }

  duelScene = () => {
    return this.scene.get('Duel') as Duel;
  };

  getDefinitionExampleView() {
    return this.definitionExampleView;
  }

  getDefinitionExampleImage() {
    return this.definitionExampleView.definitionExampleImage;
  }

  create() {
    this.editorCreate();
    this.controller.initialize();
  }

  override getBoardDropSite() {
    return this.boardDropSite;
  }

  override getHandDropSite() {
    return this.handDropSite;
  }
  override getPlayerTypingInputs() {
    return [this.typingView as PlayerTypingInput];
  }

  override getDropSiteFromCard(card: StudentGameCard) {
    return card.origin?.dropSite;
  }

  override getShouldDisplayBoardSlots(): boolean {
    return false;
  }

  override getControlScheme(preferredScheme: ControllerType) {
    // For now, disallow drag n drop for duels.
    return preferredScheme === ControllerType.KEYBOARD
      ? ControllerType.KEYBOARD
      : ControllerType.CLICK;
  }

  setupBoardForControlScheme(scheme: ControllerType) {
    if (scheme === ControllerType.KEYBOARD) {
      this.cardView.setVisible(false);
      this.typingView.setVisible(true);
    } else {
      this.cardView.setVisible(true);
      this.typingView.setVisible(false);
    }
    this.setBoardInteractive(true);
  }

  getHandCards(): StudentGameCard[] {
    return this.getAllCards().filter(
      (c) => c.origin?.dropSite === this.handDropSite,
    );
  }

  getBoardCards(): StudentGameCard[] {
    return this.getAllCards().filter(
      (c) => c.origin?.dropSite === this.boardDropSite,
    );
  }

  checkAnswer() {
    this._controller.checkAnswer();
  }

  override setBoardInteractive(isInteractable: boolean) {
    this.boardInteractive = isInteractable;
    this.submitButton.controller.makeInteractive(
      isInteractable ? this.getCanSubmit() : false,
    );
    this.repeatInstructionButton.setInteractionEnabled(isInteractable);
    this.playDefinitionButton.setInteractionEnabled(isInteractable);
    this.playWordInSentenceButton.setInteractionEnabled(isInteractable);
    this.typingView.setIsInteractive(isInteractable);
    this.getAllCards().forEach((card) => {
      card.makeInteractive(isInteractable, false);
    });
  }

  setSubmissionPossible(canSubmit: boolean) {
    this.submitButton.controller.makeInteractive(canSubmit);
  }

  override getDefinitionIconsToLoad() {
    return this.controller.getDefinitionIconsToLoad();
  }

  override getExampleIconsToLoad() {
    return this.controller.getExampleIconsToLoad();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
